// CFG.PA.A2 — tenant-visible Platform Access Log.
//
// Lists every grant of access to this tenant by BluePrint platform-admin
// staff. Read-only by design — the underlying table has a DELETE-prevention
// rule, and the API does not expose any mutation endpoints from this page.
// Visible to TENANT_ADMIN only.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { apiFetch } from '../lib/api';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

type Status = 'ACTIVE' | 'EXPIRED' | 'REVOKED';

interface AccessLogRow {
  id: string;
  tenantId: string;
  platformAdminEmail: string;
  reason: string;
  accessStart: string;
  accessExpiry: string;
  accessEnd: string | null;
  revokedReason: string | null;
  status: Status;
}

const STATUS_BADGE: Record<Status, { label: string; bg: string; fg: string }> = {
  ACTIVE:  { label: 'Active',  bg: '#dcfce7', fg: '#166534' },
  EXPIRED: { label: 'Expired', bg: '#f3f4f6', fg: '#374151' },
  REVOKED: { label: 'Revoked', bg: '#fee2e2', fg: '#991b1b' },
};

export default function SettingsPlatformAccessLogPage() {
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const [rows, setRows] = useState<AccessLogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'ALL' | Status>('ALL');

  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  useEffect(() => {
    if (!tenantId || !isAdmin) return;
    apiFetch<AccessLogRow[]>(`/admin/${tenantId}/platform-access-log`)
      .then(setRows)
      .catch(err => setError(err.message ?? 'Failed to load platform access log.'));
  }, [tenantId, isAdmin]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (filter === 'ALL') return rows;
    return rows.filter(r => r.status === filter);
  }, [rows, filter]);

  if (tenantLoading) {
    return (
      <div style={{ display: 'flex' }}>
        <Sidebar />
        <main style={{ padding: 24 }}>Loading…</main>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ display: 'flex' }}>
        <Sidebar />
        <main style={{ padding: 24, maxWidth: 600 }}>
          <h1 style={{ fontSize: 24, marginBottom: 12 }}>Platform Access Log</h1>
          <p style={{ color: '#6b7280' }}>
            This page is only visible to tenant administrators.
            <Link to="/settings" style={{ marginLeft: 8 }}>← Back to Settings</Link>
          </p>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <main style={{ padding: 24, maxWidth: 1100 }}>
        <Link to="/settings" style={{ color: '#6b7280', fontSize: 14 }}>← Back to Settings</Link>
        <h1 style={{ fontSize: 28, margin: '12px 0 4px' }}>Platform Access Log</h1>
        <p style={{ color: '#4b5563', maxWidth: 720, lineHeight: 1.5 }}>
          BluePrint support staff can be granted time-limited access to your workspace
          to investigate issues you raise. Every grant is recorded here permanently —
          this log cannot be edited or deleted, even by BluePrint staff.
        </p>

        <div style={{ marginTop: 20, marginBottom: 12, display: 'flex', gap: 8 }}>
          {(['ALL', 'ACTIVE', 'EXPIRED', 'REVOKED'] as const).map(opt => (
            <button
              key={opt}
              onClick={() => setFilter(opt)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                background: filter === opt ? '#7c3aed' : 'white',
                color: filter === opt ? 'white' : '#374151',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {opt === 'ALL' ? 'All' : STATUS_BADGE[opt].label}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: 12, borderRadius: 6, color: '#991b1b' }}>
            {error}
          </div>
        )}

        {!rows && !error && <p style={{ color: '#6b7280' }}>Loading log entries…</p>}

        {rows && rows.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', background: '#f9fafb', borderRadius: 8 }}>
            <p style={{ color: '#374151', fontSize: 16, margin: 0 }}>
              No BluePrint staff have accessed your workspace.
            </p>
          </div>
        )}

        {filtered && filtered.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={th}>Date / Time</th>
                <th style={th}>Admin</th>
                <th style={th}>Reason</th>
                <th style={th}>Granted until</th>
                <th style={th}>Status</th>
                <th style={th}>Closed at</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const badge = STATUS_BADGE[r.status];
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={td}>{new Date(r.accessStart).toLocaleString()}</td>
                    <td style={td}>{r.platformAdminEmail}</td>
                    <td style={{ ...td, maxWidth: 360 }}>{r.reason}</td>
                    <td style={td}>{new Date(r.accessExpiry).toLocaleString()}</td>
                    <td style={td}>
                      <span
                        style={{
                          background: badge.bg,
                          color: badge.fg,
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td style={td}>{r.accessEnd ? new Date(r.accessEnd).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}

const th: React.CSSProperties = { padding: '10px 12px', fontWeight: 600, fontSize: 13, color: '#374151' };
const td: React.CSSProperties = { padding: '10px 12px', color: '#1f2937', verticalAlign: 'top' };
